<template>
  <modal
    class="x-filter-modal"
    :open="open"
    :saveText="$t('ui.xfilters.add_filter_confirm')"
    :cancelText="null"
    :clickOutsideClose="true"
    @cancel="closeModal"
    @save="addCondition"
  >
    <span class="x-filter-modal-label">
      {{ filterDefinition.label }}
    </span>

    <!-- Predicate choices -->
    <span class="x-filter-modal-predicates" v-if="Array.isArray(filterDefinition.predicate)">
      <q-radio
        class="x-filter-modal-predicate-item"
        v-for="(predName, index) in filterDefinition.predicate"
        :key="index"
        v-model="data.predicate"
        :val="predName"
        :label="$t('ui.xfilters.conditions.predicates.' + predName)"
        :disable="hasNone"
      />
    </span>

    <!-- Select a single value via an auto-complete field -->
    <span class="x-filter-modal-text" v-if="filterDefinition.type == 'text'">
      <auto-complete
        v-if="filterDefinition.auto_complete"
        class="x-filter-modal-autocomplete"
        :model="filterDefinition.auto_complete"
        type="text"
        label="empty"
        name="value"
        :item="data"
        init="none"
        :disabled="hasNone"
        @change="autoSelect"
        :autofocus="true"
        :acMinCharacters="3"
      ></auto-complete>

      <field
        v-else
        v-model="data.value"
        :disabled="hasNone"
        :autofocus="true"
      />
    </span>

    <span class="x-filter-modal-set" v-if="filterDefinition.type == 'set'">

      <span v-if="filterDefinition.predicate == 'eq'">
        <!-- Select a single value via a select box -->
        <span v-if="filterDefinition.method == 'select'">
          <field
            type="select"
            :collection="filterDefinition.options"
            v-model="data.value"
            :disabled="hasNone"
            :autofocus="true"
          />
        </span>

        <!-- Select a single country via select box -->
        <span v-else-if="filterDefinition.attribute == 'country'">
          <country-select
            v-model="data.value"
            :disabled="hasNone"
            :autofocus="true"
            :language="$store.getters.getCurrentLanguage"
            :params="{favorites: ['DE']}"
          />
        </span>

        <!-- Select a single value via radio buttons -->
        <span v-else>
          <q-radio
            v-for="(option, index) in filterDefinition.options"
            :key="index"
            v-model="data.value"
            :val="option.value"
            :label="option.name"
            :disable="hasNone"
          />
        </span>
      </span>

      <!-- Select one or more values via chips -->
      <span v-if="filterDefinition.method == 'chips'" class="x-filter-modal-chips">
        <q-field>
          <q-chips-input
            v-model="data.value"
            :disable="hasNone"
            :autofocus="true"
            hide-underline
            close-icon="close"
          />
        </q-field>
      </span>
      
      <!-- Select one or more values via checkboxes -->
      <span v-if="['in', 'has_any', 'has_all'].includes(filterDefinition.predicate)">
        <q-checkbox
          v-for="(option, index) in filterDefinition.options"
          :key="index"
          v-model="data.value"
          :val="option.value"
          :label="option.name"
          :disable="hasNone"
          class="x-filter-modal-checkbox"
        />
      </span>
    </span>

    <!-- Select date ranges via date pickers -->
    <span class="x-filter-modal-date field" v-if="filterDefinition.type == 'date'">
      <field
        v-if="this.enableRelativeRanges()"
        type="select"
        :collection="filterDefinition.options"
        v-model="data.dateRange"
        :autofocus="true"
        @input="dateRangeChange"
      />
      <label>{{ $t('ui.xfilters.xfiltermodal.date_from') }}</label>
      <datepicker
        v-model="data.value"
        :key="'from' + key"
        :clear-button="true"
        :full-month-name="true"
        format="d MMMM, yyyy"
        :disabled-dates="{ from: data.value2 }"
        @input="dateChanged"
        :language="getDatePickerLanguage()"
      ></datepicker>
      <label>{{ $t('ui.xfilters.xfiltermodal.date_to') }}</label>
      <datepicker
        v-model="data.value2"
        :key="'to' + key"
        :clear-button="true"
        :full-month-name="true"
        format="d MMMM, yyyy"
        :disabled-dates="{ to: data.value }"
        @input="dateChanged"
        :language="getDatePickerLanguage()"
      ></datepicker>
    </span>

    <span class="x-filter-modal-has-none" v-if="filterDefinition.include_none != null">
      <q-checkbox
        class="x-filter-modal-checkbox"
        v-model="hasNone"
        color="primary"
        :label="filterDefinition.include_none"
      />
    </span>
  </modal>
</template>

<script>
import Modal from "@/components/helpers/Layout/NewModal"
import Datepicker from "vuejs-datepicker"
import moment from "moment"
import Field from "@/components/helpers/DataInput/FormField"
import AutoComplete from "@/components/helpers/DataDisplay/AutoComplete"
import CountrySelect from "@/components/helpers/DataInput/CountrySelect"

export default {
  components: { Datepicker, Field, AutoComplete, CountrySelect, Modal },
  props: {
    filterDefinition: {
      type: Object,
      required: true,
    },
    isOpen: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      open: false,
      hasNone: false,
      data: {
        value_id: null,
        value: '',
        predicate: null,
        dateRange: 'today',
      },
      key: 0,
    }
  },
  beforeMount() {
    // Override the default icons used for the chip close button
    this.$q.icon.chip.close = "close"

    if (['in', 'has_any', 'has_all'].includes(this.filterDefinition.predicate) || this.filterDefinition.method == 'chips') {
      this.data.value = []
    }
    if (this.filterDefinition.type == 'date') {
      // If no 'today' range exists, use the first as the default
      let exists = this.filterDefinition.options.some((option) => {
        return option.value == 'today'
      })
      if (!exists) {
        this.data.dateRange = this.filterDefinition.options[0].value
      }
    }
    if (typeof this.filterDefinition.default_predicate != 'undefined') {
      this.data.predicate = this.filterDefinition.default_predicate
    }
    this.open = this.isOpen
  },
  mounted() {   
  },
  methods: {
    closeModal() {
      this.open = false
    },
    openModal(filterCondition) {
      this.open = true
    },
    addCondition() {
      this.$emit('addCondition', this.filterDefinition, this.data, this.hasNone)
      this.closeModal()
    },
    autoSelect(value) {
      this.data.value_id = value.id
      this.data.value = value.name
    },
    enableRelativeRanges() {
      // If not specified, then default is true
      return (typeof this.filterDefinition.enableRelativeRanges == 'undefined'
              || this.filterDefinition.enableRelativeRanges == true)
    },
    dateRangeChange() {
      // If a date range is selected, then this is a relative date,
      // and the static from/to dates are cleared
      if (this.data.dateRange != 'custom') {
        this.data.value = null
        this.data.value2 = null
        this.key++
      }
    },
    dateChanged() {
      // If either of the from/to dates are selected,
      // then we considere them fixed dates, so the relative range is custom
      this.data.dateRange = 'custom'
    },
  },
  watch: {
    isOpen: {
      handler() {
        this.open = this.isOpen
      }
    },
  },
}
</script>

<style lang="sass" scoped>
</style>



// WEBPACK FOOTER //
// XFilterModal.vue?54dc7cfc